import subscription from 'api/sellsyaccountmanager/subscription.js';

const enabledPlugins = [
    {
        id: 'stock',
        title: window.i18n.__('Stock'),
        description: window.i18n.__(
            'Gérez vos stocks, vos entrepôts, vos commandes et soyez alerté en cas de rupture.',
        ),
        icon: 'nav-stock',
    },
    {
        id: 'support',
        title: window.i18n.__('Support'),
        description: window.i18n.__(
            'Affectez les tickets en un clic à vos collaborateurs, proposez un service personnalisé à vos clients.',
        ),
        icon: 'nav-support',
    },
    {
        id: 'cashflow',
        title: window.i18n.__('Trésorerie'),
        description: window.i18n.__(
            'Visualisez votre trésorerie en temps réel et réalisez des prévisions.',
        ),
        subtitle: {
            STARTER: 'Standard',
            PRO: 'Evolution',
            BUSINESS: 'Elite',
        },
        icon: 'nav-cash-flow',
    },
    {
        id: 'marketing',
        title: window.i18n.__('Marketing'),
        description: window.i18n.__(
            'Profitez d’une solution Marketing puissante et intégrée à votre CRM Sellsy pour décupler votre potentiel de croissance.',
        ),
        subtitle: {
            STARTER: 'Standard',
            PRO: 'Evolution',
            BUSINESS: 'Elite',
        },
        icon: 'nav-marketing',
    },
];

const store = {
    namespaced: true,
    state: {
        loaded: false,
        licenses: [],
        versionedLicenses: [],
        availableLicenses: [],
        plugins: {},
        plan: '',
        versionedPlans: [],
        serviceStartDate: '',
        serviceEndDate: '',
        interval: '',
        amount: {},
        billingAddress: {},
        billingContact: {},
        credits: [],
        staffs: [],
        staffPage: 0,
        staffLimit: 0,
        renewCartId: null,
        renewActiveDate: null,
        paymentMethod: '',
        nextPaymentDate: null,
    },
    getters: {
        enabledPlugins: () => {
            return enabledPlugins;
        },
        activePlugins: (state, getters) => {
            return _.filter(getters.enabledPlugins, (plugin) => !!state.plugins[plugin.id]);
        },
        hasRenewCart: (state) => {
            return !!state.renewCartId;
        },
    },
    mutations: {
        setLicenses: function (state, licenses) {
            state.licenses = licenses;
        },
        updateAvailableLicenses: function (state, licenses) {
            state.availableLicenses = licenses;
        },
        setPlugins: function (state, plugins) {
            state.plugins = plugins;
        },
        setPlan: function (state, plan) {
            state.plan = state.versionedPlans.find(
                (currentPlan) => currentPlan.code.toUpperCase() === plan,
            )?.label;
        },
        setServiceStartDate: function (state, serviceStartDate) {
            state.serviceStartDate = serviceStartDate;
        },
        setServiceEndDate: function (state, serviceEndDate) {
            state.serviceEndDate = serviceEndDate;
        },
        setInterval: function (state, interval) {
            state.interval = interval;
        },
        setAmount: function (state, amount) {
            state.amount = amount;
        },
        setBillingAddress: function (state, billingAddress) {
            state.billingAddress = billingAddress;
        },
        setBillingContact: function (state, billingContact) {
            state.billingContact = billingContact;
        },
        setCredits: function (state, credits) {
            state.credits = credits;
        },
        addStaffs: function (state, staffs) {
            state.staffs.push(...staffs);
        },
        addStaff: function (state, staff) {
            state.staffs.push(staff);
        },
        increaseStaffPage: function (state) {
            state.staffPage++;
        },
        setStaffLimit: function (state, limit) {
            state.staffLimit = limit;
        },
        setLicenseForStaff: function (state, payload) {
            const staff = state.staffs.find((staff) => staff.id === payload.staffId);
            const pos = state.staffs.map((staff) => staff.id).indexOf(payload.staffId);
            staff.license = payload.licenseId;
            state.staffs.splice(pos, 1, staff);
        },
        setPaymentMethod: function (state, payload) {
            state.paymentMethod = payload;
        },
        increaseLicenseAvailable: function (state, licenseId) {
            const license = state.licenses.find((license) => license.id === licenseId);
            const pos = state.licenses.map((license) => license.id).indexOf(licenseId);
            license.available++;
            state.licenses.splice(pos, 1, license);
        },
        decreaseLicenseAvailable: function (state, licenseId) {
            const license = state.licenses.find((license) => license.id === licenseId);
            const pos = state.licenses.map((license) => license.id).indexOf(licenseId);
            license.available--;
            state.licenses.splice(pos, 1, license);
        },
        loaded: function (state) {
            state.loaded = true;
        },
        setRenewCart: function (state, payload) {
            state.renewCartId = payload.renew_cart?.id;
            state.renewActiveDate = payload.renew_cart?.period_appliance_start;
        },
        setNextPaymentDate: function (state, nextPaymentDate) {
            state.nextPaymentDate = nextPaymentDate;
        },
    },
    actions: {
        setAvailableLicenses: function ({ state, commit }, nullAllowed = true) {
            const names = state.versionedLicenses.filter((name) => {
                return !!state.licenses.find((license) => license.code === name.code);
            });

            const licenses = names.map((name) => {
                const license = state.licenses.find((license) => license.code === name.code);
                return {
                    value: license.id,
                    label: name.label,
                    disabled: license.available === 0,
                };
            });
            if (nullAllowed) {
                const minimum = [
                    {
                        value: 'null',
                        label: window.sellsyapp.$l10n.__('Aucune licence'),
                    },
                ];
                commit('updateAvailableLicenses', minimum.concat(licenses));
                return;
            }
            commit('updateAvailableLicenses', licenses);
        },
        getSubscription: function ({ commit, dispatch }) {
            return subscription.getSubscription().then(({ data: subscriptionDetails }) => {
                if (
                    'plan' in subscriptionDetails.billing_settings &&
                    subscriptionDetails.billing_settings.plan !== null
                ) {
                    const [plan, interval] = subscriptionDetails.billing_settings.plan.split('_');
                    commit('setPlan', plan);
                    commit('setInterval', interval.toLowerCase());
                    commit(
                        'setAmount',
                        subscriptionDetails.billing_settings.amounts[interval.toLowerCase()],
                    );
                }
                if (
                    'service_start_date' in subscriptionDetails.billing_settings &&
                    subscriptionDetails.billing_settings.service_start_date !== null
                ) {
                    commit(
                        'setServiceStartDate',
                        subscriptionDetails.billing_settings.service_start_date.split('T')[0],
                    );
                }
                if (
                    'service_end_date' in subscriptionDetails.billing_settings &&
                    subscriptionDetails.billing_settings.service_end_date !== null
                ) {
                    commit(
                        'setServiceEndDate',
                        subscriptionDetails.billing_settings.service_end_date.split('T')[0],
                    );
                }
                commit('setNextPaymentDate', subscriptionDetails.next_payment_date);
                commit('setLicenses', subscriptionDetails.licenses);
                commit('setPlugins', subscriptionDetails.plugins);
                commit('setBillingAddress', subscriptionDetails.billing_address);
                commit('setBillingContact', subscriptionDetails.billing_contact);
                commit('setRenewCart', subscriptionDetails);
                commit('setPaymentMethod', subscriptionDetails.billing_settings.payment_method);
                commit('loaded');
                dispatch('setAvailableLicenses');
            });
        },
        addStaffs: function ({ commit, state }) {
            return subscription.getStaffs(state.staffPage * state.staffLimit).then((response) => {
                const staffs = response.data.data.map((staff) => {
                    staff.license = null;
                    if (staff._embed.licenses !== null && staff._embed.licenses[0].enabled) {
                        staff.license = staff._embed.licenses[0].id;
                    }
                    delete staff._embed;
                    return staff;
                });
                commit('addStaffs', staffs);

                commit('increaseStaffPage');
                commit('setStaffLimit', response.data.pagination.limit);
                return response;
            });
        },
        createStaff: function ({ commit, state, dispatch }, { data, licenses }) {
            let staff = {
                id: data.id,
                firstname: data.firstname,
                lastname: data.lastname,
                avatar: data.avatar,
                position: data.position,
                license: licenses?.length > 0 ? licenses[0].id : null,
                loading: false,
            };
            dispatch('getSubscription');
            commit('addStaff', staff);
        },
        setLicenseForStaff: function ({ commit, state }, payload) {
            return subscription
                .putLicenseForStaff(payload.staffId, payload.licenseId)
                .then((response) => {
                    if (state.staffs.length !== 0) {
                        const staff = state.staffs.find((staff) => staff.id === payload.staffId);
                        /* for any raison, vuex replace null by 'null', so need to double check */
                        if (staff.license !== null && staff.license !== 'null') {
                            commit('increaseLicenseAvailable', staff.license);
                        }
                        commit('setLicenseForStaff', payload);
                    }

                    if (
                        payload.previousLicenseId &&
                        payload.previousLicenseId !== null &&
                        payload.previousLicenseId !== 'null'
                    ) {
                        commit('increaseLicenseAvailable', payload.previousLicenseId);
                    }

                    if (response.data.length > 0) {
                        commit('decreaseLicenseAvailable', response.data[0].id);
                    }

                    return response;
                });
        },
    },
};

const factory = (subscriptionManagerDictionary = {}) => {
    return {
        ...store,
        state: {
            ...store.state,
            versionedPlans: subscriptionManagerDictionary.plans,
            versionedLicenses: subscriptionManagerDictionary.licenses,
        },
    };
};

export { store as default, factory };
